// install-prompt.ts — suppress Chromium's automatic PWA install prompt.
//
// Backlit makes every Lit glow installable (D1, universal), so Chromium fires
// `beforeinstallprompt` and may surface its own automatic "install app" prompt
// (chiefly on Android; desktop keeps the address-bar install icon regardless).
// The platform default is to NOT nag: the SDK captures the event and calls
// preventDefault(), which quashes the automatic prompt. The glow stays
// installable — the browser's own address-bar icon / menu install path is
// unaffected — this only stops the proactive prompt.
//
// Chromium-only by construction, so nothing is needed for the other engines:
// Safari never fires `beforeinstallprompt` (its install is a manual
// Share → Add to Home Screen — nothing to suppress) and Firefox doesn't fire it
// either, so this listener is inert everywhere but Chromium.
//
// Guard order mirrors sw-register.ts / heartbeat.ts (isBacklitHost() fails OPEN
// in a non-browser harness, so the window existence check MUST come first):
//   1. already started → no-op (idempotent against a double <script src>)
//   2. non-browser (no window, or no addEventListener) → no-op
//   3. off a deployed glow host → no-op

import { isBacklitHost } from "./host.js";

// started gates double-init (index.ts evaluates once, but a double-load would
// re-run it), so the listener is added at most once. Module-level like
// sw-register.ts's `started`.
let started = false;

// initInstallPrompt is the auto-start entry point, called once from index.ts
// after initServiceWorker(). It registers a `beforeinstallprompt` listener that
// preventDefault()s Chromium's automatic install prompt, best-effort and silent.
export function initInstallPrompt(): void {
  if (started) return;
  if (typeof window === "undefined") return;
  if (typeof window.addEventListener !== "function") return;
  if (!isBacklitHost()) return;

  started = true;

  try {
    window.addEventListener("beforeinstallprompt", (event: Event) => {
      // Suppress Chromium's automatic install prompt. The glow stays installable
      // via the browser's own affordances (address-bar icon / menu); we only
      // stop the proactive nag. We do not stash the event — v1 offers no
      // app-triggered install, so there is nothing to defer it for.
      event.preventDefault();
    });
  } catch {
    /* ignore — addEventListener threw synchronously (exotic harness) */
  }
}

// _resetInstallPromptForTest clears the module-level state so each test starts
// clean — mirrors sw-register.ts's _resetSWRegisterForTest.
export function _resetInstallPromptForTest(): void {
  started = false;
}
